import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import { required, emailFormatValid } from '../../util/validators';

import css from './NewsletterSignup.module.css';

/**
 * NewsletterSignup — email subscription form for Mailchimp.
 *
 * @param {string} [className]
 * @param {string[]} [tags]  - optional Mailchimp tags to add to subscriber
 */
const NewsletterSignup = ({ className, tags = [] }) => {
  const intl = useIntl();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async values => {
    setError(null);
    try {
      const res = await fetch('/api/mailchimp/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email, tags }),
      });
      if (!res.ok) throw new Error('Subscription failed');
      setSubmitted(true);
    } catch (err) {
      setError(intl.formatMessage({ id: 'NewsletterSignup.error' }));
    }
  };

  const emailRequired = required(intl.formatMessage({ id: 'NewsletterSignup.emailRequired' }));
  const emailValid = emailFormatValid(intl.formatMessage({ id: 'NewsletterSignup.emailInvalid' }));

  if (submitted) {
    return (
      <div className={classNames(css.root, className, css.success)}>
        <FormattedMessage id="NewsletterSignup.success" />
      </div>
    );
  }

  return (
    <div className={classNames(css.root, className)}>
      <FinalForm
        onSubmit={handleSubmit}
        render={({ handleSubmit, submitting, invalid }) => (
          <form onSubmit={handleSubmit} className={css.form}>
            <Field name="email" validate={v => emailRequired(v) || emailValid(v)}>
              {({ input, meta }) => (
                <div className={css.inputWrapper}>
                  <input
                    {...input}
                    type="email"
                    placeholder={intl.formatMessage({ id: 'NewsletterSignup.emailPlaceholder' })}
                    className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                  />
                  {meta.touched && meta.error && (
                    <span className={css.fieldError}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
            <button type="submit" disabled={submitting || invalid} className={css.submitBtn}>
              <FormattedMessage id="NewsletterSignup.submitButton" />
            </button>
          </form>
        )}
      />
      {error && <div className={css.errorMsg}>{error}</div>}
    </div>
  );
};

export default NewsletterSignup;
